import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, Scissors, Waves } from "lucide-react";
import { RangeSlider } from "@/components/ui/range-slider";

function mediaUrl(u) {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

function fmt(t) {
  if (!Number.isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function AudioTrimControls({ draft, onChange }) {
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(Number(draft.audio_duration) || 0);
  const [range, setRange] = useState([
    Number(draft.audio_trim_start) || 0,
    Number(draft.audio_trim_end) || Number(draft.audio_duration) || 0,
  ]);
  const [fadeIn, setFadeIn] = useState(Number(draft.audio_fade_in) || 0);
  const [fadeOut, setFadeOut] = useState(Number(draft.audio_fade_out) || 0);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRange([
      Number(draft.audio_trim_start) || 0,
      Number(draft.audio_trim_end) || Number(draft.audio_duration) || 0,
    ]);
    setFadeIn(Number(draft.audio_fade_in) || 0);
    setFadeOut(Number(draft.audio_fade_out) || 0);
    setDuration(Number(draft.audio_duration) || 0);
    setDirty(false);
  }, [draft.draft_id, draft.audio_url, draft.audio_trim_start, draft.audio_trim_end, draft.audio_duration, draft.audio_fade_in, draft.audio_fade_out]);

  // When playing, loop inside the selected [start, end] slice.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      if (el.currentTime >= range[1]) {
        el.currentTime = range[0];
      }
    };
    const onEnd = () => { setPlaying(false); };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
    };
  }, [range]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.currentTime = range[0];
      el.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const onSlide = (vals) => {
    // If a thumb hit the other, nudge.
    let [a, b] = vals;
    if (a > b - 0.5) a = Math.max(0, b - 0.5);
    setRange([a, b]);
    setDirty(true);
    // While dragging, keep playback preview in sync.
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.currentTime = a;
    }
  };

  const applyTrim = async () => {
    setSaving(true);
    try {
      await api.patch(`/drafts/${draft.draft_id}/audio/trim`, {
        start: Number(range[0].toFixed(2)),
        end: Number(range[1].toFixed(2)),
        fade_in: Number(Number(fadeIn).toFixed(2)),
        fade_out: Number(Number(fadeOut).toFixed(2)),
      });
      toast.success("Trim + fade applied — video re-rendered.");
      setDirty(false);
      await onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Trim failed";
      toast.error(typeof detail === "string" ? detail : "Trim failed");
    } finally {
      setSaving(false);
    }
  };

  const audioSrc = mediaUrl(draft.audio_url);
  if (!audioSrc) return null;

  const sliceLen = Math.max(0, range[1] - range[0]);

  return (
    <div className="mt-3 border border-white/[0.08] bg-black/40 p-3 rounded-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono text-white/70 flex items-center gap-2">
          <Scissors size={12} className="text-[#FF3B00]" />
          Trim music
        </div>
        <div className="label-mono text-white/40 font-mono">
          {fmt(range[0])} – {fmt(range[1])} <span className="text-white/25">·</span> {fmt(sliceLen)} loop
        </div>
      </div>

      <audio
        ref={audioRef}
        src={audioSrc}
        preload="metadata"
        onLoadedMetadata={(e) => {
          if (!duration && Number.isFinite(e.currentTarget.duration)) {
            const d = e.currentTarget.duration;
            setDuration(d);
            setRange((r) => [r[0], r[1] || d]);
          }
        }}
        onPause={() => setPlaying(false)}
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={togglePlay}
          className="h-8 w-8 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-colors"
          data-testid={`audio-trim-play-${draft.draft_id}`}
        >
          {playing ? <Pause size={13} /> : <Play size={13} />}
        </button>
        <div className="flex-1">
          <RangeSlider
            value={range}
            max={duration || 1}
            step={0.1}
            minStepsBetweenThumbs={1}
            onValueChange={onSlide}
            data-testid={`audio-trim-slider-${draft.draft_id}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4 pt-3 border-t border-white/[0.05]">
        <FadeKnob
          label="Fade in"
          value={fadeIn}
          onChange={(v) => { setFadeIn(v); setDirty(true); }}
          videoDur={Number(draft.video_duration) || 0}
          testId={`audio-fade-in-${draft.draft_id}`}
        />
        <FadeKnob
          label="Fade out"
          value={fadeOut}
          onChange={(v) => { setFadeOut(v); setDirty(true); }}
          videoDur={Number(draft.video_duration) || 0}
          testId={`audio-fade-out-${draft.draft_id}`}
        />
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="label-mono text-white/35">
          The chosen slice will loop across the full video ({fmt(Number(draft.video_duration) || 0)}).
        </div>
        <button
          type="button"
          onClick={applyTrim}
          disabled={!dirty || saving}
          className="btn-amber-small"
          data-testid={`audio-trim-apply-${draft.draft_id}`}
        >
          {saving ? "Re-rendering…" : "Apply trim"}
        </button>
      </div>
    </div>
  );
}

function FadeKnob({ label, value, onChange, videoDur, testId }) {
  const max = Math.max(0.5, Math.min(8, (videoDur || 0) / 2));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="label-mono text-white/65 flex items-center gap-1.5">
          <Waves size={11} className="text-[#FF3B00]" /> {label}
        </div>
        <div className="font-mono text-xs text-white">{Number(value).toFixed(1)}s</div>
      </div>
      <input
        type="range"
        min={0}
        max={max}
        step={0.1}
        value={Math.min(value, max)}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#FF3B00]"
        data-testid={testId}
      />
    </div>
  );
}
